
import React, { useEffect } from "react";
import { Mail, Phone, MapPin, ArrowRight, ArrowUpRight, ShieldCheck, GraduationCap, Users, Award, Sparkles, Building2, Landmark, UserRoundCheck } from "lucide-react";
import { motion } from "framer-motion";
import LogoFallback from "/assets/img/bubbanlogo.jpg";

export default function Proctor({
  logoSrc = LogoFallback,
  accent = "#115e32",
  pageTitle = "Proctorial Body | Bandarban University",
}) {
  useEffect(() => {
    document.title = pageTitle;
  }, [pageTitle]);

  const proctors = [
    {
      id: 2,
      image: "/assets/teacher/2014.jpeg",
      name: "Ching Chi Prue Sukey",
      designation_name: "Assistant Proctor",
      department_name: "Proctor Office",
      mobile_number: "",
      email: "Sukey.marma06@gmail.com",
    },
    {
      id: 3,
      image: "/assets/teacher/2009.jpeg",
      name: "Udwab Kundu",
      designation_name: "Assistant Proctor",
      department_name: "Proctor Office",
      mobile_number: "01797141748",
      email: "udwabkundu33@gmail.com",
    },
  ];

  const fadeUp = {
    hidden: { opacity: 0, y: 35 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.7, ease: "easeOut" },
    },
  };

  const stagger = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15 },
    },
  };

  const cardAnimation = {
    hidden: { opacity: 0, y: 35, scale: 0.96 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: { duration: 0.65, ease: "easeOut" },
    },
  };

  return (
    <div className="min-h-screen overflow-hidden bg-[#f6faf7] text-gray-800">

      {/* =========================================
          HERO SECTION
      ========================================= */}

      <section
        className="relative isolate overflow-hidden"
        style={{
          background: `linear-gradient(135deg, ${accent} 0%, #0b4027 50%, #062b1b 100%)`,
        }}
      >
        {/* Background Decoration */}

        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{
              duration: 45,
              repeat: Infinity,
              ease: "linear",
            }}
            className="absolute -top-48 -right-40 h-[600px] w-[600px] rounded-full border border-white/10"
          />

          <motion.div
            animate={{
              scale: [1, 1.2, 1],
              x: [0, 30, 0],
            }}
            transition={{
              duration: 12,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            className="absolute -bottom-48 -left-40 h-[600px] w-[600px] rounded-full bg-emerald-300/10 blur-3xl"
          />

          <div
            className="absolute inset-0 opacity-[0.05]"
            style={{
              backgroundImage:
                "radial-gradient(#ffffff 1px, transparent 1px)",
              backgroundSize: "28px 28px",
            }}
          />
        </div>

        <div className="relative mx-auto max-w-7xl px-6 py-20 sm:px-8 md:py-28 lg:px-16">

          <motion.div
            initial="hidden"
            animate="visible"
            variants={stagger}
            className="mx-auto max-w-4xl text-center"
          >
            {/* University Logo */}

            <motion.div
              variants={fadeUp}
              className="relative mx-auto mb-8 inline-flex"
            >
              <div className="absolute -inset-4 rounded-full bg-white/10 blur-xl" />

              <div className="relative h-24 w-24 rounded-full bg-white p-3 shadow-2xl ring-4 ring-white/20 md:h-28 md:w-28">
                <img
                  src={logoSrc}
                  alt="Bandarban University"
                  className="h-full w-full object-contain"
                />
              </div>
            </motion.div>

            {/* Badge */}

            <motion.div variants={fadeUp}>
              <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-5 py-2 text-sm font-semibold text-white/90 backdrop-blur-md">
                <ShieldCheck
                  size={17}
                  className="text-green-300"
                />
                Student Welfare & Discipline
              </span>
            </motion.div>

            {/* Heading */}

            <motion.h1
              variants={fadeUp}
              className="mt-7 text-4xl font-extrabold leading-tight tracking-tight text-white sm:text-5xl md:text-6xl lg:text-7xl"
            >
              Proctorial
              <span className="block bg-gradient-to-r from-green-200 via-white to-green-300 bg-clip-text text-transparent">
                Body
              </span>
            </motion.h1>

            <motion.p
              variants={fadeUp}
              className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-green-50/80 md:text-lg"
            >
              Ensuring a safe, disciplined, and supportive
              academic environment through responsible
              leadership and student-centered services.
            </motion.p>

            {/* Buttons */}

            <motion.div
              variants={fadeUp}
              className="mt-9 flex flex-wrap justify-center gap-4"
            >
              <a
                href="#proctor"
                className="group inline-flex items-center gap-3 rounded-xl bg-white px-7 py-3.5 font-bold text-emerald-900 shadow-xl transition-all duration-300 hover:bg-green-50"
              >
                Meet Our Proctor

                <ArrowRight
                  size={18}
                  className="transition-transform group-hover:translate-x-1"
                />
              </a>

              <a
                href="#team"
                className="inline-flex items-center gap-3 rounded-xl border border-white/20 bg-white/10 px-7 py-3.5 font-semibold text-white backdrop-blur-md transition-all duration-300 hover:bg-white/20"
              >
                Our Team

                <ArrowUpRight size={18} />
              </a>
            </motion.div>
          </motion.div>
        </div>

        {/* Bottom Wave */}

        <div className="absolute bottom-0 left-0 w-full leading-none">
          <svg
            viewBox="0 0 1440 120"
            xmlns="http://www.w3.org/2000/svg"
            className="h-16 w-full md:h-24"
            preserveAspectRatio="none"
          >
            <path
              d="M0,64 C240,120 480,0 720,48 C960,96 1200,24 1440,64 L1440,120 L0,120 Z"
              fill="#f6faf7"
            />
          </svg>
        </div>
      </section>

      {/* =========================================
          PROCTOR PROFILE
      ========================================= */}

      <section
        id="proctor"
        className="px-6 py-16 sm:px-8 md:py-24 lg:px-16"
      >
        <div className="mx-auto max-w-7xl">

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeUp}
            className="mb-14 text-center"
          >
            <span className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[3px] text-emerald-700">
              <Landmark size={17} />
              University Administration
            </span>

            <h2 className="mt-4 text-3xl font-extrabold text-gray-900 md:text-4xl">
              Office of the Proctor
            </h2>

            <p className="mx-auto mt-4 max-w-2xl leading-relaxed text-gray-500">
              Dedicated to maintaining discipline, promoting
              student welfare, and supporting a positive
              university environment.
            </p>
          </motion.div>

          {/* Main Proctor Card */}

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.15 }}
            variants={cardAnimation}
            className="grid overflow-hidden rounded-[32px] border border-gray-100 bg-white shadow-xl shadow-emerald-900/5 lg:grid-cols-5"
          >

            {/* Image */}

            <div className="relative min-h-[400px] overflow-hidden bg-emerald-900 lg:col-span-2 lg:min-h-[550px]">

              <motion.img
                src={`${import.meta.env.VITE_API_SOURCE_URL}/assets/teacher/2011.jpeg`}
                alt="Hasan Mahmood"
                initial={{ scale: 1.08 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 1.2 }}
                className="absolute inset-0 h-full w-full object-cover object-top"
              />

              <div className="absolute inset-0 bg-gradient-to-t from-emerald-950/90 via-emerald-950/10 to-transparent" />

              <div className="absolute left-6 top-6">
                <span className="inline-flex items-center gap-2 rounded-full bg-white/90 px-4 py-2 text-xs font-bold text-emerald-900 shadow-lg backdrop-blur-md">
                  <Award size={15} />
                  Chief Proctor
                </span>
              </div>

              <div className="absolute bottom-0 left-0 right-0 p-7 md:p-9">

                <div className="mb-5 h-1 w-12 rounded-full bg-green-400" />

                <h3 className="text-3xl font-extrabold text-white md:text-4xl">
                  Hasan Mahmood
                </h3>

                <p className="mt-3 font-semibold text-green-200">
                  Proctor
                </p>

                <p className="mt-2 text-sm text-white/70">
                  Bandarban University
                </p>

              </div>
            </div>

            {/* Profile Details */}

            <div className="flex flex-col justify-center p-7 md:p-10 lg:col-span-3 lg:p-12">

              <span className="text-sm font-bold uppercase tracking-widest text-emerald-700">
                Leadership & Student Welfare
              </span>

              <h2 className="mt-4 text-3xl font-extrabold text-gray-900">
                Hasan Mahmood
              </h2>

              <p className="mt-3 text-lg font-semibold text-emerald-700">
                Proctor
              </p>

              <p className="mt-2 text-gray-500">
                Bandarban University
              </p>

              <div className="mt-7 h-1 w-20 rounded-full bg-emerald-500" />

              <p className="mt-7 leading-8 text-gray-600">
                The Proctorial Body works to ensure discipline,
                security, and a supportive learning environment
                for all students. Through guidance and
                responsible administration, the office
                contributes to the overall development
                of the university community.
              </p>

              {/* Contact Details */}

              <div className="mt-8 space-y-4">

                <a
                  href="tel:01552542652"
                  className="group flex items-center gap-4 rounded-2xl border border-gray-100 bg-gray-50 p-4 transition-all duration-300 hover:border-emerald-200 hover:bg-emerald-50"
                >
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white text-emerald-700 shadow-sm transition group-hover:bg-emerald-700 group-hover:text-white">
                    <Phone size={21} />
                  </div>

                  <div>
                    <p className="text-xs font-bold uppercase tracking-wider text-gray-400">
                      Contact Number
                    </p>

                    <p className="mt-1 font-semibold text-gray-800">
                      01552542652
                    </p>
                  </div>

                  <ArrowUpRight
                    size={18}
                    className="ml-auto text-gray-300 transition group-hover:text-emerald-700"
                  />
                </a>

                <a
                  href="mailto:hasan@bubban.ac.bd"
                  className="group flex items-center gap-4 rounded-2xl border border-gray-100 bg-gray-50 p-4 transition-all duration-300 hover:border-emerald-200 hover:bg-emerald-50"
                >
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white text-emerald-700 shadow-sm transition group-hover:bg-emerald-700 group-hover:text-white">
                    <Mail size={21} />
                  </div>

                  <div className="min-w-0">
                    <p className="text-xs font-bold uppercase tracking-wider text-gray-400">
                      Email Address
                    </p>

                    <p className="mt-1 break-all font-semibold text-gray-800">
                      hasan@bubban.ac.bd
                    </p>
                  </div>

                  <ArrowUpRight
                    size={18}
                    className="ml-auto shrink-0 text-gray-300 transition group-hover:text-emerald-700"
                  />
                </a>

              </div>

              {/* Profile Links */}

              <div className="mt-8">

                <p className="mb-4 text-sm font-bold text-gray-800">
                  Academic & Research Profiles
                </p>

                <div className="flex flex-wrap gap-2">

                  {[
                    {
                      name: "Google Scholar",
                      color: "#4285F4",
                    },
                    {
                      name: "ResearchGate",
                      color: "#00CCBB",
                    },
                    {
                      name: "ORCID",
                      color: "#A6CE39",
                    },
                    {
                      name: "Scopus",
                      color: "#E87524",
                    },
                    {
                      name: "Web of Science",
                      color: "#6D4BC3",
                    },
                  ].map((item, i) => (
                    <motion.a
                      key={item.name}
                      href="#"
                      target="_blank"
                      rel="noreferrer"
                      whileHover={{ y: -3 }}
                      initial={{ opacity: 0, y: 12 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.08 }}
                      className="inline-flex items-center gap-2 rounded-xl border border-gray-100 bg-white px-3 py-2.5 text-xs font-semibold text-gray-600 shadow-sm transition hover:shadow-md sm:text-sm"
                    >
                      <span
                        className="h-2 w-2 rounded-full"
                        style={{ backgroundColor: item.color }}
                      />

                      {item.name}

                      <ArrowUpRight
                        size={13}
                        className="text-gray-300"
                      />
                    </motion.a>
                  ))}

                </div>
              </div>

            </div>
          </motion.div>
        </div>
      </section>

      {/* =========================================
          ASSISTANT PROCTORS
      ========================================= */}

      <section
        id="team"
        className="relative overflow-hidden bg-white px-6 py-20 sm:px-8 md:py-28 lg:px-16"
      >

        <div className="pointer-events-none absolute -right-32 -top-32 h-96 w-96 rounded-full bg-emerald-50 blur-3xl" />

        <div className="relative mx-auto max-w-7xl">

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="mb-14 text-center"
          >
            <span className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[3px] text-emerald-700">
              <Users size={17} />
              Our Team
            </span>

            <h2 className="mt-4 text-3xl font-extrabold text-gray-900 md:text-4xl">
              Assistant Proctors
            </h2>

            <p className="mx-auto mt-4 max-w-2xl leading-relaxed text-gray-500">
              Meet the dedicated team supporting student
              discipline, welfare, and university activities.
            </p>
          </motion.div>

          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.15 }}
            className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3"
          >

            {proctors.map((faculty, i) => (
              <motion.div
                key={faculty.id}
                variants={cardAnimation}
                whileHover={{ y: -9 }}
                className="group overflow-hidden rounded-[28px] border border-gray-100 bg-white shadow-lg shadow-emerald-900/5 transition-all duration-500 hover:shadow-2xl hover:shadow-emerald-900/10"
              >

                {/* Image */}

                <div className="relative h-80 overflow-hidden bg-emerald-900">

                  <motion.img
                    src={`${import.meta.env.VITE_API_SOURCE_URL}${faculty.image}`}
                    alt={faculty.name}
                    initial={{ scale: 1.05 }}
                    whileInView={{ scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 1 }}
                    className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                  />

                  <div className="absolute inset-0 bg-gradient-to-t from-emerald-950/80 via-transparent to-transparent" />

                  <div className="absolute left-5 top-5">
                    <span className="inline-flex items-center gap-2 rounded-full bg-white/90 px-4 py-2 text-xs font-bold text-emerald-900 shadow-md backdrop-blur-md">
                      <UserRoundCheck size={15} />
                      Assistant Proctor
                    </span>
                  </div>

                  <div className="absolute bottom-0 left-0 right-0 p-6">

                    <div className="mb-4 h-1 w-10 rounded-full bg-green-400" />

                    <h3 className="text-2xl font-extrabold text-white">
                      {faculty.name}
                    </h3>

                    <p className="mt-2 text-sm font-medium text-green-200">
                      {faculty.designation_name}
                    </p>

                  </div>
                </div>

                {/* Details */}

                <div className="p-6">

                  <div className="flex items-center gap-3">

                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700">
                      <Building2 size={20} />
                    </div>

                    <div>
                      <p className="text-xs font-bold uppercase tracking-wider text-gray-400">
                        Department
                      </p>

                      <p className="mt-1 font-semibold text-gray-800">
                        {faculty.department_name}
                      </p>
                    </div>

                  </div>

                  {/* Phone */}

                  {faculty.mobile_number && (
                    <a
                      href={`tel:${faculty.mobile_number}`}
                      className="mt-5 flex items-center gap-3 text-sm text-gray-500 transition hover:text-emerald-700"
                    >
                      <Phone
                        size={17}
                        className="shrink-0 text-emerald-600"
                      />

                      {faculty.mobile_number}
                    </a>
                  )}

                  {/* Email */}

                  {faculty.email && (
                    <a
                      href={`mailto:${faculty.email}`}
                      className="mt-4 flex items-center gap-3 text-sm text-gray-500 transition hover:text-emerald-700"
                    >
                      <Mail
                        size={17}
                        className="shrink-0 text-emerald-600"
                      />

                      <span className="break-all">
                        {faculty.email}
                      </span>
                    </a>
                  )}

                  {/* Footer */}

                  <div className="mt-6 border-t border-gray-100 pt-5">

                    <a
                      href={`mailto:${faculty.email}`}
                      className="group/link inline-flex items-center gap-2 text-sm font-bold text-emerald-700 transition hover:text-emerald-900"
                    >
                      Contact Assistant Proctor

                      <ArrowRight
                        size={17}
                        className="transition-transform group-hover/link:translate-x-1"
                      />
                    </a>

                  </div>

                </div>
              </motion.div>
            ))}

          </motion.div>
        </div>
      </section>

      {/* =========================================
          VISION / MISSION / GOALS
      ========================================= */}

      <section className="relative overflow-hidden px-6 py-20 sm:px-8 md:py-28 lg:px-16">

        <div className="pointer-events-none absolute -left-32 -bottom-32 h-96 w-96 rounded-full bg-green-100/60 blur-3xl" />

        <div className="relative mx-auto max-w-7xl">

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="mb-14 text-center"
          >
            <span className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[3px] text-emerald-700">
              <Sparkles size={17} />
              Our Commitment
            </span>

            <h2 className="mt-4 text-3xl font-extrabold text-gray-900 md:text-4xl">
              Vision, Mission & Goals
            </h2>

            <p className="mx-auto mt-4 max-w-2xl leading-relaxed text-gray-500">
              Promoting academic excellence, student
              development, and a responsible university
              community.
            </p>
          </motion.div>

          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            className="grid gap-6 md:grid-cols-2 lg:grid-cols-3"
          >

            {[
              {
                title: "Vision",
                subtitle: "A Leading Institution",
                description:
                  "To become a leading institution of higher learning in Asia.",
                icon: GraduationCap,
                number: "01",
              },
              {
                title: "Mission",
                subtitle: "Education & Leadership",
                description:
                  "To nurture creativity, research, and leadership through quality education.",
                icon: ShieldCheck,
                number: "02",
              },
              {
                title: "Goals",
                subtitle: "Innovation & Collaboration",
                description:
                  "Expanding research opportunities and strengthening global partnerships.",
                icon: Users,
                number: "03",
              },
            ].map((item) => {
              const Icon = item.icon;

              return (
                <motion.div
                  key={item.number}
                  variants={cardAnimation}
                  whileHover={{ y: -9 }}
                  className="group relative overflow-hidden rounded-3xl border border-gray-100 bg-white p-8 shadow-lg shadow-emerald-900/5 transition-all duration-500 hover:shadow-2xl"
                >

                  <span className="absolute right-6 top-4 text-6xl font-black text-emerald-900/[0.04] transition group-hover:text-emerald-900/[0.08]">
                    {item.number}
                  </span>

                  <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-800 transition-all duration-300 group-hover:rotate-6 group-hover:bg-emerald-800 group-hover:text-white">
                    <Icon size={29} />
                  </div>

                  <h3 className="mt-7 text-2xl font-extrabold text-gray-900">
                    {item.title}
                  </h3>

                  <p className="mt-2 text-sm font-semibold text-emerald-700">
                    {item.subtitle}
                  </p>

                  <p className="mt-4 leading-7 text-gray-500">
                    {item.description}
                  </p>

                  <div className="absolute bottom-0 left-0 h-1 w-0 bg-emerald-700 transition-all duration-500 group-hover:w-full" />

                </motion.div>
              );
            })}

          </motion.div>
        </div>
      </section>

      {/* =========================================
          CONTACT SECTION
      ========================================= */}

      <section
        id="contact"
        className="relative isolate overflow-hidden"
        style={{
          background: `linear-gradient(135deg, ${accent} 0%, #0b4027 55%, #062b1b 100%)`,
        }}
      >

        {/* Background Decoration */}

        <div className="absolute inset-0 pointer-events-none overflow-hidden">

          <motion.div
            animate={{ scale: [1, 1.15, 1] }}
            transition={{
              duration: 10,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            className="absolute -right-40 -top-40 h-[500px] w-[500px] rounded-full bg-white/[0.04] blur-3xl"
          />

          <div
            className="absolute inset-0 opacity-[0.04]"
            style={{
              backgroundImage:
                "radial-gradient(#ffffff 1px, transparent 1px)",
              backgroundSize: "24px 24px",
            }}
          />

        </div>

        <div className="relative mx-auto max-w-7xl px-6 py-16 sm:px-8 md:py-24 lg:px-16">

          <motion.div
            initial={{ opacity: 0, y: 35 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="text-center"
          >

            <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl border border-white/20 bg-white/10 text-green-200">
              <Building2 size={30} />
            </div>

            <h2 className="text-3xl font-extrabold text-white md:text-4xl">
              Contact Proctorial Office
            </h2>

            <p className="mx-auto mt-4 max-w-2xl leading-relaxed text-green-50/70">
              Connect with the Proctorial Body for student
              welfare, discipline, and university-related
              inquiries.
            </p>

          </motion.div>

          {/* Contact Information */}

          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="mt-12 grid gap-5 md:grid-cols-3"
          >

            {/* Address */}

            <motion.div
              variants={cardAnimation}
              className="flex items-start gap-4 rounded-2xl border border-white/10 bg-white/[0.07] p-6 backdrop-blur-md transition-all duration-300 hover:bg-white/[0.12]"
            >

              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white/10 text-green-200">
                <MapPin size={23} />
              </div>

              <div>
                <h3 className="font-bold text-white">
                  Office Address
                </h3>

                <p className="mt-2 text-sm leading-7 text-green-50/70">
                  K.B Road, Shualok, Post Office: Shualok -
                  4600, Bandarban Sadar, Bangladesh
                </p>
              </div>

            </motion.div>

            {/* Phone */}

            <motion.a
              variants={cardAnimation}
              href="tel:+8801318305900"
              className="flex items-start gap-4 rounded-2xl border border-white/10 bg-white/[0.07] p-6 backdrop-blur-md transition-all duration-300 hover:bg-white/[0.12]"
            >

              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white/10 text-green-200">
                <Phone size={23} />
              </div>

              <div>
                <h3 className="font-bold text-white">
                  Contact Number
                </h3>

                <p className="mt-2 text-sm text-green-50/70">
                  +880 1318-305900
                </p>
              </div>

            </motion.a>

            {/* Email */}

            <motion.a
              variants={cardAnimation}
              href="mailto:admission@bubban.ac.bd"
              className="flex items-start gap-4 rounded-2xl border border-white/10 bg-white/[0.07] p-6 backdrop-blur-md transition-all duration-300 hover:bg-white/[0.12]"
            >

              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white/10 text-green-200">
                <Mail size={23} />
              </div>

              <div>
                <h3 className="font-bold text-white">
                  Email Address
                </h3>

                <p className="mt-2 break-all text-sm text-green-50/70">
                  admission@bubban.ac.bd
                </p>
              </div>

            </motion.a>

          </motion.div>

          {/* Appointment Button */}

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
            className="mt-10 text-center"
          >

            <a
              href="mailto:admission@bubban.ac.bd?subject=Request%20Appointment"
              className="group inline-flex items-center gap-3 rounded-xl bg-white px-8 py-4 font-bold text-emerald-900 shadow-xl transition-all duration-300 hover:bg-green-50"
            >
              Request Appointment

              <ArrowRight
                size={19}
                className="transition-transform group-hover:translate-x-1"
              />
            </a>

          </motion.div>

        </div>
      </section>

    </div>
  );
}
